import { WEBSITE_DOMAIN } from '@application/constants';
import { addTrailingSlash, formatApiPrice, useRouter } from '@application/utils';
import type { LitterGetLitterQuery } from '@gql/graphql';
import { CountryCode } from '@gql/graphql';
import { useMemo } from 'react';

export const useSeo = (litter?: NonNullable<LitterGetLitterQuery['findManyLitter']>[number]) => {
  const { translate, translatedRoute } = useRouter();

  const breedName = useMemo(() => {
    return litter?.dogBreed?.translations?.[0]?.name ?? '';
  }, [litter?.dogBreed?.translations]);

  const baseBreedName = useMemo(() => {
    return litter?.dogBreed?.baseBreedName ?? litter?.dogBreed?.name ?? breedName;
  }, [breedName, litter?.dogBreed?.baseBreedName, litter?.dogBreed?.name]);

  const price = useMemo(() => {
    return formatApiPrice({ ...litter }) ?? '';
  }, [litter]);

  const title = useMemo(() => {
    return translate('litterDetails.dynamicSeo.title')
      .replace('$breed', breedName ?? '')
      .replace(
        '$country',
        translate(`enum.country.${litter?.breeder.account.address?.country ?? CountryCode.BE}`, {
          defaultValue: '',
        }),
      );
  }, [breedName, litter?.breeder.account.address?.country, translate]);

  const description = useMemo(() => {
    return translate('litterDetails.dynamicSeo.description')
      .replace('$breed', breedName ?? '')
      .replace('$price', price)
      .replace(' €', '')
      .replace(
        '$country',
        translate(`enum.country.${litter?.breeder.account.address?.country ?? CountryCode.BE}`, {
          defaultValue: '',
        }),
      );
  }, [breedName, litter?.breeder.account.address?.country, price, translate]);

  const keywords = useMemo(() => {
    return translate('litterDetails.dynamicSeo.keywords').replace('$breed', breedName ?? '');
  }, [breedName, translate]);

  const canonicalUrl = useMemo(() => {
    const url = `${WEBSITE_DOMAIN}${translatedRoute('litterList')}${encodeURI(baseBreedName)}`;
    return addTrailingSlash(url);
  }, [baseBreedName, translatedRoute]);

  return { title, description, keywords, canonicalUrl };
};
